import axios from "axios";
import config from "../../../config";
import type {
  CleaningDataResponse,
  FieldFilterDataResponse,
  DistinctValuesRequest,
  DistinctValuesResponse,
  EntityFieldItem,
  TableInfo,
  EntityFieldsResponse,
  ChangeValueRequest,
  SplitValueRequest,
  ChartRequest,
  ChartResponse,
} from "../types/api-types";

export type {
  DataTypeInfo,
  FieldData,
  EntityData,
  CleaningDataItem,
  CleaningDataResponse,
  FieldFilterDataItem,
  FieldFilterDataResponse,
  DistinctValueItem,
  CategoryResult,
  DistinctValuesResponse,
  ClientType,
  ClaimType,
  ClientInformationForDistinct,
  TemplateInformation,
  ClientForDistinct,
  DistinctValuesRequest,
  EntityFieldItem,
  TableInfo,
  EntityFieldsResponse,
  ChangeValueRequest,
  ColumnDetail,
  SplitValueItem,
  SplitValueRequest,
  ChartRequest,
  ChartResponse,
  ChartNode,
  ChartEdge,
  EntityType,
  NodeDetail,
} from "../types/api-types";

const apiUrl = config.api;

export const getCleaningData = async (): Promise<CleaningDataResponse> => {
  const response = await axios.get<CleaningDataResponse>(`${apiUrl}/cleaning`);
  return response.data;
};

export const getFieldFilterData = async (
  fieldId: number,
): Promise<FieldFilterDataResponse> => {
  const response = await axios.get<FieldFilterDataResponse>(
    `${apiUrl}/cleaning/field/${fieldId}/filter`,
  );
  return response.data;
};

export const getDistinctValues = async (
  fieldId: number,
  requestBody: DistinctValuesRequest,
): Promise<DistinctValuesResponse> => {
  const response = await axios.post<DistinctValuesResponse>(
    `${apiUrl}/cleaning/field/${fieldId}/distinct-values`,
    requestBody,
  );
  return response.data;
};

// API response structure from /fields (internal types)
interface ApiFieldItem {
  id: number;
  columnId: number;
  columnName: string;
  dataType: string;
  maximumLength: number;
  nullable: boolean;
  show: number;
}

interface ApiTableData {
  tableId: number;
  tableName: string;
  fields: ApiFieldItem[];
}

interface ApiEntityFieldsResponse {
  results: ApiTableData[];
}

export const getEntityFields = async (
  entityId?: number,
  sourceFieldId?: number,
): Promise<EntityFieldsResponse> => {
  const response = await axios.get<ApiEntityFieldsResponse>(`${apiUrl}/fields`);

  // Extract all available tables
  const availableTables: TableInfo[] = response.data.results.map((table) => ({
    tableId: table.tableId,
    tableName: table.tableName,
  }));

  // If entityId is provided, filter to that specific table
  let fields: EntityFieldItem[] = [];

  if (entityId !== undefined || sourceFieldId !== undefined) {
    const tableData = response.data.results.find((table) =>
      sourceFieldId !== undefined
        ? table.fields.some((field) => field.id === sourceFieldId)
        : table.tableId === entityId,
    );

    fields =
      tableData?.fields.map((field) => ({
        id: field.id,
        name: field.columnName,
      })) || [];
  } else {
    // If no entityId, return all fields from all tables
    fields = response.data.results.flatMap((table) =>
      table.fields.map((field) => ({
        id: field.id,
        name: field.columnName,
      })),
    );
  }

  return {
    results: fields,
    availableTables,
  };
};

export const markValuesAsValid = async (
  fieldId: number,
  values: string[],
): Promise<void> => {
  await axios.post(`${apiUrl}/cleaning/field/${fieldId}/valid-values`, values);
};

export const changeValues = async (
  fieldId: number,
  changes: ChangeValueRequest[],
): Promise<void> => {
  await axios.post(
    `${apiUrl}/cleaning/field/${fieldId}/change-values`,
    changes,
  );
};

export const moveValues = async (
  fieldId: number,
  newFieldId: number,
  values: string[],
): Promise<void> => {
  await axios.post(
    `${apiUrl}/cleaning/field/${fieldId}/${newFieldId}/move-values`,
    values,
  );
};

export const reclassifyValues = async (
  fieldId: number,
  newFieldId: number,
  values: string[],
): Promise<void> => {
  await axios.post(
    `${apiUrl}/cleaning/field/${fieldId}/${newFieldId}/reclassify-values`,
    values,
  );
};

export const purgeValues = async (
  fieldId: number,
  values: string[],
): Promise<void> => {
  await axios.post(`${apiUrl}/cleaning/field/${fieldId}/purge-values`, values);
};

export const purgeRows = async (
  fieldId: number,
  values: string[],
): Promise<void> => {
  await axios.post(`${apiUrl}/cleaning/field/${fieldId}/purge-row`, values);
};

export const splitValue = async (
  fieldId: number,
  request: SplitValueRequest,
): Promise<void> => {
  await axios.post(`${apiUrl}/cleaning/field/${fieldId}/split-value`, request);
};

export const getItemChart = async (
  request: ChartRequest,
  signal?: AbortSignal,
): Promise<ChartResponse> => {
  const response = await axios.post<ChartResponse>(
    `${apiUrl}/cleaning/chart`,
    request,
    { signal },
  );
  return response.data;
};

export const cleanseField = async (
  fieldId: number,
  templateId: number,
): Promise<void> => {
  await axios.post(`${apiUrl}/cleaning/field/${fieldId}/${templateId}`);
};
